import ms from "ms";
import { OrgConfigs, ConfigKey } from "@whatslly/types/OrgConfigs";
import { DLPPolicy } from "@whatslly/types/dlp/DLPPolicy";
import { UserPermissions } from "./granularPermissions";
import { DLPRule } from "@whatslly/types/dlp/DLPRule";
import { PREFIX_SF_TUVIS_SUBJECT } from "./constants";
import { orgPartnerValues } from "@whatslly/types/OrgPartner";
import { FeatureFlagRelatedIntegrations } from "@whatslly/types/messages-server/destinations";
import { UserType } from "@whatslly/types/User";
import { ORG_TYPE_OPTIONS } from "./constants/organizationTypes";

export const DEVELOPERS_USER_IDS_FOR_SUPER_SENSITIVE_CONFIGS = [
  "0058b00000GuCwSAAV", // Dor / Amir
  "005d2000000iSQPAA2", // Alon
  "0058d000002SWpfAAG", // Hezi
  "005Qy000008qHHtIAM", // Amir
  "0055f000003WxTLAA0", // Ron
];

export type FeatureFlagCategoryType =
  | "entity.create"
  | "entity.edit"
  | "entity.display"
  | "search"
  | "layoutLimits"
  | "actions"
  | "apps"
  | "notifications"
  | "general"
  | "autoSyncActivities"
  | "autoSyncCases"
  | "orgAdminMessageSync"
  | "orgAdminRules"
  | "orgAdminUserManagement"
  | "tuvisAdminChannels"
  | "tuvisAdminFeatures"
  | "tuvisAdminMessagesSync"
  | "tuvisAdminSecurity";

interface featureFlagBase {
  key: string;
  category: FeatureFlagCategoryType;
  hide?: (orgConfigs: OrgConfigs, user?: UserType) => boolean;
  updatePermission?: UserPermissions;
  viewPermission?: UserPermissions;
  onlySuperAdminCanUpdate?: boolean;
  relatedReporters?: FeatureFlagRelatedIntegrations["type"][];
  conflictingKeys?: ConfigKey[];
}

interface BooleanFeatureFlagType extends featureFlagBase {
  type: "boolean";
  defaultValue: boolean;
}

interface NumericFeatureFlagType extends featureFlagBase {
  type: "numeric";
  defaultValue?: number;
  minNumber?: number;
  maxNumber?: number;
}

interface SelectFeatureFlagType extends featureFlagBase {
  type: "select";
  defaultValue?: string;
  options: string[];
}

interface MultiSelectFeatureFlagType extends featureFlagBase {
  options: string[];
  type: "multiSelect";
  defaultValue: string[];
}
export interface StringArrayFeatureFlagType extends featureFlagBase {
  type: "stringArray";
  defaultValue?: string[];
  maxSelectedItems?: number;
  onlyUniqueItems?: boolean;
}

interface StringFeatureFlagType extends featureFlagBase {
  type: "string";
  defaultValue?: string;
  mask?: boolean;
}
interface EncryptedStringFeatureFlagType extends featureFlagBase {
  type: "encryptedString";
  defaultValue?: string;
}
interface DateFeatureFlagType extends featureFlagBase {
  type: "date";
  defaultValue?: string;
}

interface LegacyObjectFeatureFlagType<T extends Record<string, string | number | boolean>, K extends keyof T = keyof T> extends featureFlagBase {
  type: "legacyObject";
  objectSubType: T[K] extends string ? "string" : T[K] extends number ? "numeric" : T[K] extends boolean ? "boolean" : never;
  defaultValue?: T;
}

interface PerUserObjectFeatureFlagType<T extends Record<string, string | number | boolean>, K extends keyof T = keyof T> extends featureFlagBase {
  type: "perUserObject";
  objectSubType: T[K] extends string ? "string" : T[K] extends number ? "numeric" : T[K] extends boolean ? "boolean" : never;
  defaultValue?: T;
}

interface GenericFeatureFlagType<T> extends featureFlagBase {
  type: "other";
  defaultValue?: T;
}

export type OrgConfigFeaturesType =
  | BooleanFeatureFlagType
  | NumericFeatureFlagType
  | SelectFeatureFlagType
  | StringFeatureFlagType
  | EncryptedStringFeatureFlagType
  | StringArrayFeatureFlagType
  | DateFeatureFlagType
  | MultiSelectFeatureFlagType
  | LegacyObjectFeatureFlagType<Record<string, any>>
  | PerUserObjectFeatureFlagType<Record<string, any>>
  | GenericFeatureFlagType<any>;

type SelectTypeFlags =
  | "serverlocation"
  | "showselectivechatsync"
  | "default_sidecar_frequency"
  | "defaultLanguage"
  | "org_partner"
  | "available_crm"
  | "orgType";
type MultiSelectTypeFlags = "default_favorite_actions" | "disable_opportunities_default_layout_field_for_entities";

export type ConfigDeclaration<T extends ConfigKey> =
  Exclude<OrgConfigs[T], undefined> extends number
    ? NumericFeatureFlagType
    : T extends SelectTypeFlags
      ? SelectFeatureFlagType
      : T extends MultiSelectTypeFlags
        ? MultiSelectFeatureFlagType
        : T extends "POCExpiration"
          ? DateFeatureFlagType
          : T extends "dlpPolicies"
            ? GenericFeatureFlagType<DLPPolicy[]>
            : T extends "dlpRules"
              ? GenericFeatureFlagType<DLPRule[]>
              : Exclude<OrgConfigs[T], undefined> extends string
                ? StringFeatureFlagType
                : Exclude<OrgConfigs[T], undefined> extends boolean
                  ? BooleanFeatureFlagType
                  : Exclude<OrgConfigs[T], undefined> extends string[]
                    ? StringArrayFeatureFlagType
                    : T extends "incidents_summary_recipients"
                      ? GenericFeatureFlagType<{ email: string; userId?: string; sendOnlyIncidentsRelatedToUser?: boolean }>
                      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        Exclude<OrgConfigs[T], undefined> extends Record<string, any>
                        ?
                            | LegacyObjectFeatureFlagType<Exclude<OrgConfigs[T], undefined>>
                            | PerUserObjectFeatureFlagType<Exclude<OrgConfigs[T], undefined>>
                        : never;

export type ConfigDeclarations = {
  [P in ConfigKey]: Omit<ConfigDeclaration<P>, "key">;
};

export const DEFAULT_MAX_LAYOUT = 10;
export const DEFAULT_FAVORITES_ACTIONS = [
  "view",
  "createTask",
  "createActivity",
  "createCase",
  "createLead",
  "createContact",
  "createPersonAccount",
  "meeting",
];

/**
 * This object is here to manage the feature flags in the UI
 * to add a new feature flag, add a new entry in this object while key is the api name of the feature flag and value is the feature flag settings
 * each key should have translation to name and helpText in the platform translation files
 */
export const orgConfigsObject: ConfigDeclarations = {
  hide_meta_ai_button_in_desktop: {
    type: "boolean",
    category: "actions",
    defaultValue: false,
  },
  show_only_wap_channels_in_desktop: {
    type: "boolean",
    category: "actions",
    defaultValue: false,
  },
  hide_wap_channels_button_in_desktop: {
    type: "boolean",
    category: "actions",
    defaultValue: false,
  },
  hide_block_contact_button: {
    type: "boolean",
    category: "actions",
    defaultValue: false,
  },
  hide_delete_conversation_button: {
    type: "boolean",
    category: "actions",
    defaultValue: false,
  },
  desktop_add_paired_phone_on_login: {
    type: "boolean",
    category: "orgAdminUserManagement",
    defaultValue: false,
    conflictingKeys: ["force_user_phone"],
  },
  max_paired_phones_on_login: {
    type: "numeric",
    category: "orgAdminUserManagement",
    defaultValue: 4,
    minNumber: 1,
    maxNumber: 4,
  },
  replace_phone_on_limit_exceeded: {
    type: "boolean",
    category: "orgAdminUserManagement",
    defaultValue: false,
    conflictingKeys: ["force_user_phone"],
  },
  limit_date_range_of_messages: {
    type: "numeric",
    defaultValue: 0,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["user_management.view"] | UserPermissions["messages_analytics.view"] | UserPermissions["sidecars.view"],
  },
  phones_blocked_from_sync: {
    type: "stringArray",
    category: "tuvisAdminMessagesSync",
    hide: () => true,
  },
  isActive: {
    type: "boolean",
    defaultValue: true,
    category: "tuvisAdminFeatures",
    hide: () => true,
  },
  paidLicences: {
    type: "numeric",
    defaultValue: 0,
    category: "tuvisAdminChannels",
  },
  exceededLicences: {
    type: "numeric",
    defaultValue: 0,
    category: "tuvisAdminChannels",
  },
  isPOC: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    hide: () => true,
  },
  POCExpiration: {
    type: "date",
    category: "tuvisAdminFeatures",
    defaultValue: "",
    hide: () => true,
  },
  org_partner: {
    type: "select",
    defaultValue: "None",
    options: orgPartnerValues,
    category: "tuvisAdminFeatures",
    hide: () => true,
  },
  defaultLanguage: {
    type: "select",
    defaultValue: "ENGLISH",
    options: ["ENGLISH", "SPANISH", "PORTUGUESE"],
    category: "tuvisAdminFeatures",
    hide: () => true,
  },
  leadscreate: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  contactscreate: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  taskscreate: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  casescreate: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  activitiescreate: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  create_person_account: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  leadsdisplay: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  contactsdisplay: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  casesdisplay: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  activitiesdisplay: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  tasksdisplay: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  contactoppsdisplay: {
    type: "boolean",
    defaultValue: false,
    category: "entity.display",
  },
  show_person_account: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  oppsdisplay: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  hideleadprogresstopbar: {
    type: "boolean",
    defaultValue: false,
    category: "entity.display",
  },
  enableautosynchidebutton: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
  },
  enableautosyncfiles: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
  },
  // TODO perfer destinationMaxFileSize
  autosynclimittomaxfilesize: {
    type: "numeric",
    defaultValue: 4400000,
    category: "tuvisAdminMessagesSync",
  },
  hidesearchonmessenger: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  appshidegooglemeet: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  appshidemsteams: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  hideopportunitytopbar: {
    type: "boolean",
    defaultValue: false,
    category: "entity.display",
  },
  hidesearchongoogle: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  hidesearchonlinkedin: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  appshidedocusign: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  enableautosyncalwayson: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
    viewPermission: UserPermissions["settings.view_general"],
    updatePermission: UserPermissions["settings.update_general"],
  },
  appshideall: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  appshideallvideoapps: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  allowuserstograntsupportaccess: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  appshidezoom: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  serverlocation: {
    type: "select",
    defaultValue: "us1",
    options: ["us1", "br1", "eu1"],
    category: "tuvisAdminSecurity",
    hide: () => true,
  },
  available_crm: {
    type: "select",
    defaultValue: "none",
    options: ["salesforce", "dynamics", "hubspot", "pipedrive"],
    category: "general",
    hide: () => true,
  },
  orgType: {
    type: "select",
    defaultValue: "none",
    options: ORG_TYPE_OPTIONS.map((option) => option.value),
    category: "general",
    hide: () => true,
  },
  hide_widget_close_button: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  hide_forward_button: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  hide_bulk_messages: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  allow_embedded_signup_with_partner: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
  },
  hideedittemplatesbuttons: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  hidenewtemplatebutton: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  fileuploadshidecompletely: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  hideaccountbox: {
    type: "boolean",
    defaultValue: false,
    category: "entity.display",
  },
  hidetemplatesbutton: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  multichannellimitchannels: {
    type: "numeric",
    defaultValue: 2,
    minNumber: 1,
    maxNumber: 5,
    category: "tuvisAdminFeatures",
  },
  enable_limit_number_of_contacts_engaged: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  limit_number_of_contacts_engaged: {
    type: "numeric",
    defaultValue: 20,
    category: "orgAdminRules",
  },
  enable_limit_number_of_msgs_to_unresponsive_contact: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  limit_number_of_msgs_to_unresponsive_contact: {
    type: "numeric",
    defaultValue: 3,
    category: "orgAdminRules",
  },
  multi_channel_limit_channels_per_profile: {
    type: "legacyObject",
    category: "tuvisAdminMessagesSync",
    objectSubType: "numeric",
    hide: () => true,
  },
  hidenavigatetoaddress: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  hidesearchonfacebook: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  hidecallbutton: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  auto_sync_groups_default: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
    viewPermission: UserPermissions["settings.view_general"],
    updatePermission: UserPermissions["settings.update_general"],
  },
  auto_sync_to_duplicate_records: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
  },
  hide_whatsapp_filters: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  allow_inviting_users: {
    type: "boolean",
    defaultValue: true,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["destinations.view"],
  },
  show_chat_now_action: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  auto_sync_activities__enabled: {
    type: "boolean",
    defaultValue: false,
    category: "autoSyncActivities",
  },
  auto_sync_activities__show_run_now_button: {
    type: "boolean",
    hide: (orgConfigs) => !orgConfigs.auto_sync_activities__enabled,
    defaultValue: false,
    category: "autoSyncActivities",
  },
  auto_sync_activities__task_completed_status: {
    type: "string",
    hide: (orgConfigs) => !orgConfigs.auto_sync_activities__enabled,
    category: "autoSyncActivities",
  },
  auto_sync_activities__task_subject: {
    type: "string",
    hide: (orgConfigs) => !orgConfigs.auto_sync_activities__enabled,
    defaultValue: "Tuvis Chat",
    category: "autoSyncActivities",
  },
  auto_sync_activities__hide_message_content: {
    type: "boolean",
    hide: (orgConfigs) => !orgConfigs.auto_sync_activities__enabled,
    defaultValue: false,
    category: "autoSyncActivities",
  },
  auto_sync_activities__limit_to_profile: {
    type: "stringArray",
    hide: (orgConfigs) => !orgConfigs.auto_sync_activities__enabled,
    category: "autoSyncActivities",
  },
  auto_sync_cases_enabled: {
    type: "boolean",
    defaultValue: false,
    category: "autoSyncCases",
  },
  auto_sync_cases_show_run_now_button: {
    type: "boolean",
    defaultValue: false,
    hide: (orgConfigs) => !orgConfigs.auto_sync_cases_enabled,
    category: "autoSyncCases",
  },
  auto_sync_cases_case_completed_status: {
    type: "string",
    hide: (orgConfigs) => !orgConfigs.auto_sync_cases_enabled,
    category: "autoSyncCases",
  },
  auto_sync_cases_case_subject: {
    type: "string",
    hide: (orgConfigs) => !orgConfigs.auto_sync_cases_enabled,
    defaultValue: "Tuvis Chat",
    category: "autoSyncCases",
  },
  auto_sync_cases_hide_message_content: {
    type: "boolean",
    hide: (orgConfigs) => !orgConfigs.auto_sync_cases_enabled,
    defaultValue: false,
    category: "autoSyncCases",
  },
  auto_sync_cases_limit_to_profile: {
    type: "stringArray",
    hide: (orgConfigs) => !orgConfigs.auto_sync_cases_enabled,
    category: "autoSyncCases",
  },
  inviter_name: {
    type: "string",
    category: "tuvisAdminChannels",
    hide: () => true,
  },

  show_chat_summarize_action: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  enable_widget_ai_assistant: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  create_case_opportunity_field_name: {
    type: "string",
    category: "entity.create",
  },
  allow_edit_layout_drag_and_drop: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  show_admin_notifications_tab: {
    type: "boolean",
    defaultValue: true,
    category: "tuvisAdminFeatures",
  },
  disable_save_as_at_desktop: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  allow_desktop_lite: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["user_management.view"] | UserPermissions["user_management.edit_user"],
  },
  allow_mobile_call_recording: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
    viewPermission: UserPermissions["user_management.view"] | UserPermissions["user_management.edit_user"] | UserPermissions["analytics.view"],
  },
  block_whatsapp_calls: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["user_management.view"] | UserPermissions["user_management.edit_user"],
  },
  block_extension_sidebar_in_desktop: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminUserManagement",
  },
  delete_crm_session_on_logout_in_desktop: {
    type: "boolean",
    defaultValue: true,
    category: "tuvisAdminSecurity",
  },
  layout_limits__new_activity: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits__new_case: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_case: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_lead: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_contact: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_task: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_activity: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_account: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_person_account: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_event: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits_opportunity: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  set_whatid__accountid__new_task_and_activity: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  allow_platform_integrations: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["destinations.view"],
  },
  layout_limits__new_event: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layout_limits__new_person_account: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layoutlimitsnewopportunity: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    minNumber: 10,
    maxNumber: 30,
    category: "layoutLimits",
  },
  auto_sync_files__allow_message_types: {
    type: "stringArray",
    category: "orgAdminMessageSync",
  },
  auto_sync_files__allow_media_mime_types: {
    type: "stringArray",
    category: "orgAdminMessageSync",
  },
  auto_sync_files__ignore_media_file_message_if_not_pass_validation: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
  },
  use_new_message_sync_code: {
    type: "boolean",
    defaultValue: true,
    category: "tuvisAdminMessagesSync",
  },
  ignore_chat_now_logic: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  auto_sync_message_updates: {
    type: "boolean",
    defaultValue: true,
    category: "orgAdminMessageSync",
  },
  auto_sync_message_on_status_change: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
  },
  default_notifications_over_due_only: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  prefix_phone_number_to_remove: {
    type: "stringArray",
    category: "tuvisAdminSecurity",
  },
  desktopForceNewTab: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  chat_now_modal_no_contact: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  use_only_wap_special_link: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  allow_create_support_case: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  blur_contact_on_block_unknown_person: {
    type: "boolean",
    defaultValue: true,
    category: "orgAdminRules",
  },
  allow_platform_global_layout: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["destinations.view"],
  },
  allow_platform_admin_notifications: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  extra_search_account_name_field: {
    type: "string",
    category: "entity.display",
  },

  allow_link_unknown_person: {
    type: "boolean",
    defaultValue: true,
    category: "entity.display",
  },
  access_audit_log: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["destinations.view"],
  },

  elite_security_token__user_agent_header: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    hide: () => true,
  },
  layoutlimitsnewtask: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    minNumber: 10,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layoutlimitsnewaccount: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    minNumber: 10,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layoutlimitsnewcontact: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    minNumber: 10,
    maxNumber: 30,
    category: "layoutLimits",
  },
  layoutlimitsnewlead: {
    type: "numeric",
    defaultValue: DEFAULT_MAX_LAYOUT,
    minNumber: 10,
    maxNumber: 30,
    category: "layoutLimits",
  },
  hidesaveasmobilecontact: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  appshidemsoutlook: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  showselectivechatsync: {
    type: "select",
    defaultValue: "Hide",
    options: ["Hide", "Enabled", "Disabled", "Enable_Without_Auto"],
    category: "orgAdminMessageSync",
  },
  show_default_auto_sync_config: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminMessagesSync",
    viewPermission:
      UserPermissions["user_management.view"] | UserPermissions["user_management.edit_user"] | UserPermissions["user_management.view_groups"],
  },
  hidesendemail: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  hidescheduleeventsaction: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  hidecalendar: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  hideglobalsearch: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  hidecrmlinks: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
  },
  showwhatsllytask: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  showwhatsllytasksicon: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  showblurmode: {
    type: "boolean",
    defaultValue: true,
    category: "orgAdminRules",
  },
  ignoreaddleadsourcecreatecontact: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  ignoreaddleadsourcecreatelead: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  trackanalytics: {
    type: "boolean",
    defaultValue: true,
    category: "tuvisAdminFeatures",
  },
  hide_create_business_account: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  search_business_account__name_field: {
    type: "string",
    defaultValue: "Name",
    category: "entity.display",
  },
  status_bar__allow_change_opp_stage: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  hide_mark_task_as_completed: {
    type: "boolean",
    defaultValue: false,
    category: "entity.display",
  },
  new_lead__phone_field: {
    type: "string",
    category: "entity.create",
  },
  new_contact__phone_field: {
    type: "string",
    category: "entity.create",
  },
  allow_to_sync_group: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
    viewPermission: UserPermissions["notifications.update"] | UserPermissions["notifications.view"],
  },
  new_lead__phone_field_prefix: {
    type: "string",
    category: "entity.create",
  },
  new_contact__phone_field_prefix: {
    type: "string",
    category: "entity.create",
  },
  block_browser_extension: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  elite_security_token: {
    type: "string",
    category: "tuvisAdminFeatures",
    mask: true,
    hide: () => true,
  },
  show_favorite_templates: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  block_unknown_person: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  block_groups_with_unknown_contacts: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  block_unknown_person_per_role: {
    type: "legacyObject",
    category: "orgAdminRules",
    objectSubType: "boolean",
  },
  disable_generate_reminder_params: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  allow_only_invited_users: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminUserManagement",
  },
  limit_sf_open_url: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  limit_open_external_url: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  limit_login_via_sf_mobile_phone: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminUserManagement",
  },
  block_change_profile_image: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  sidecars_enabled: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
    viewPermission: UserPermissions["destinations.view"] | UserPermissions["dlp.update"],
  },
  telegram_sidecars_enabled: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"],
  },
  whatsapp_sidecars_enabled: {
    type: "boolean",
    defaultValue: true,
    category: "tuvisAdminChannels",
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"],
  },
  line_sidecars_enabled: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"],
  },
  signal_sidecars_enabled: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"],
  },
  max_sidecars: {
    type: "numeric",
    defaultValue: 1,
    maxNumber: 10000,
    category: "tuvisAdminChannels",
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"],
  },
  max_devices_whatsapp: {
    type: "numeric",
    defaultValue: 0,
    maxNumber: 10000,
    category: "tuvisAdminChannels",
    viewPermission: UserPermissions["sidecars.create"] | UserPermissions["sidecars.view"],
  },
  max_devices_telegram: {
    type: "numeric",
    defaultValue: 0,
    maxNumber: 10000,
    category: "tuvisAdminChannels",
    viewPermission: UserPermissions["sidecars.create"] | UserPermissions["sidecars.view"],
  },
  max_devices_line: {
    type: "numeric",
    defaultValue: 0,
    maxNumber: 10000,
    category: "tuvisAdminChannels",
    viewPermission: UserPermissions["sidecars.create"] | UserPermissions["sidecars.view"],
  },
  max_devices_signal: {
    type: "numeric",
    defaultValue: 0,
    maxNumber: 10000,
    category: "tuvisAdminChannels",
    viewPermission: UserPermissions["sidecars.create"] | UserPermissions["sidecars.view"],
  },
  default_sidecar_frequency: {
    type: "select",
    defaultValue: "0 */3 * * *",
    options: ["0 * * * *", "0 */3 * * *", "0 */6 * * *", "0 */12 * * *", "permanent"],
    category: "tuvisAdminChannels",
    onlySuperAdminCanUpdate: true,
  },
  sidecar_invite_from_address: {
    type: "string",
    category: "tuvisAdminChannels",
    onlySuperAdminCanUpdate: true,
  },
  sidecar_invite_from_name: {
    type: "string",
    category: "tuvisAdminChannels",
    onlySuperAdminCanUpdate: true,
  },
  remote_sync_custom_error_text: {
    type: "string",
    category: "tuvisAdminChannels",
    hide: () => true,
  },
  show_analytics_ui: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  show_dashboards_ui: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  show_custom_filters_ui: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  custom_error_failed_to_query: {
    type: "string",
    category: "entity.display",
  },
  show_record_type_layout: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  enable_record_types_pick_list: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  enable_create_entities_to_not_focused_contact: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  show_saml_settings: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  filter_analytics_by_profile: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  show_chat_groups_ui: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  show_im_groups_ui: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    hide: (orgConfigs) => !orgConfigs.periodicGroupSync,
  },
  periodicGroupSync: {
    type: "boolean",
    category: "tuvisAdminMessagesSync",
    defaultValue: false,
  },
  periodicGroupSyncInterval: {
    type: "numeric",
    defaultValue: ms("1h"),
    category: "tuvisAdminMessagesSync",
    hide: (orgConfigs) => !orgConfigs.periodicGroupSync,
  },
  global_layout_per_profile: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
    hide: () => true,
  },
  return_selective_to_prev: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminMessageSync",
  },
  business_account__create: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  disable_retroactive_synchronization: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminMessagesSync",
  },
  scroll_to_earliest_message: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminMessagesSync",
  },
  syncronizeLastDays: {
    type: "numeric",
    defaultValue: 1,
    minNumber: 1,
    category: "tuvisAdminMessagesSync",
  },
  periodicAutoSync: {
    type: "boolean",
    category: "tuvisAdminFeatures",
    defaultValue: false,
  },
  periodicAutoSyncInterval: {
    type: "numeric",
    defaultValue: ms("24h"),
    category: "tuvisAdminMessagesSync",
    hide: (orgConfigs) => !orgConfigs.periodicAutoSync,
  },
  contact__allow_add_to_campaign_button: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  disable_contact_require_account: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  edit_lead: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  edit_contact: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  edit_task: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  edit_activity: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  edit_case: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  edit_person_account: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  edit_business_account: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  edit_opportunity: {
    type: "boolean",
    defaultValue: false,
    category: "entity.edit",
  },
  show_create_account_under_create_contact: {
    type: "boolean",
    defaultValue: true,
    category: "entity.create",
  },
  show_create_account_under_edit_contact: {
    type: "boolean",
    defaultValue: true,
    category: "entity.edit",
  },
  hide_create_account_option_under_create_contact: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  find_account_record_types: {
    type: "stringArray",
    category: "entity.create",
  },
  load_record_type_on_find_account: {
    type: "boolean",
    defaultValue: false,
    category: "entity.create",
  },
  hideEditLayout: {
    type: "boolean",
    defaultValue: false,
    category: "actions",
    hide: () => true,
  },
  notificationRecipients: {
    type: "stringArray",
    category: "notifications",
    hide: () => true,
    viewPermission: UserPermissions["notifications.update"] | UserPermissions["notifications.view"],
  },
  enabledNotifications: {
    type: "stringArray",
    category: "notifications",
    hide: () => true,
    viewPermission: UserPermissions["notifications.update"] | UserPermissions["notifications.view"],
  },
  query_cache_ttl: {
    type: "numeric",
    defaultValue: 0,
    category: "entity.display",
    onlySuperAdminCanUpdate: true,
  },
  show_email_attachment: {
    type: "boolean",
    defaultValue: true,
    category: "actions",
  },
  taskshideuserassignmentfield: {
    type: "boolean",
    defaultValue: false,
    category: "entity.display",
  },
  activitieshideuserassignmentfield: {
    type: "boolean",
    defaultValue: false,
    category: "entity.display",
  },
  allow_dlp: {
    type: "boolean",
    category: "tuvisAdminSecurity",
    defaultValue: false,
    viewPermission: UserPermissions["destinations.view"] | UserPermissions["analytics.view"],
    relatedReporters: ["tuvisDLP"],
    onlySuperAdminCanUpdate: true,
  },
  enable_surveillance: {
    type: "boolean",
    category: "tuvisAdminSecurity",
    defaultValue: false,
    viewPermission: UserPermissions["surveillance.view"] | UserPermissions["surveillance.update"],
    relatedReporters: ["tuvisSurveillance"],
    onlySuperAdminCanUpdate: true,
  },
  allow_audio_transcribes: {
    type: "boolean",
    category: "tuvisAdminFeatures",
    defaultValue: false,
    viewPermission: UserPermissions["destinations.view"],
  },
  allow_dlp_ai: {
    type: "boolean",
    category: "tuvisAdminSecurity",
    defaultValue: false,
    viewPermission: UserPermissions["dlp.update"] | UserPermissions["dlp.view"],
    onlySuperAdminCanUpdate: true,
  },
  dlp_audio: {
    type: "boolean",
    category: "tuvisAdminSecurity",
    defaultValue: false,
    viewPermission: UserPermissions["dlp.update"] | UserPermissions["dlp.view"],
  },
  //TODO remove after migration
  dlpPolicies: {
    type: "other",
    category: "tuvisAdminSecurity",
    hide: () => true,
    defaultValue: [],
    updatePermission: UserPermissions["dlp.update"],
    viewPermission: UserPermissions["dlp.update"] | UserPermissions["dlp.view"] | UserPermissions["incident.view"],
  },
  //TODO remove after migration
  dlpRules: {
    type: "other",
    category: "tuvisAdminSecurity",
    hide: () => true,
    defaultValue: [],
    updatePermission: UserPermissions["dlp.update"],
    viewPermission: UserPermissions["dlp.update"] | UserPermissions["dlp.view"],
    onlySuperAdminCanUpdate: true,
  },
  allow_waba: {
    type: "boolean",
    category: "tuvisAdminChannels",
    defaultValue: false,
    viewPermission: UserPermissions["destinations.view"],
  },
  allow_twilio: {
    type: "boolean",
    category: "tuvisAdminChannels",
    defaultValue: false,
    viewPermission: UserPermissions["destinations.view"] | UserPermissions["analytics.view"],
  },
  allow_imessage: {
    type: "boolean",
    category: "tuvisAdminChannels",
    defaultValue: false,
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"] |
      UserPermissions["user_management.view"],
  },
  allow_telegram: {
    type: "boolean",
    category: "tuvisAdminChannels",
    defaultValue: false,
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"] |
      UserPermissions["user_management.view"],
  },
  new_agent_mode: {
    type: "boolean",
    category: "tuvisAdminFeatures",
    defaultValue: false,
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"],
  },
  allow_messages_analytics: {
    type: "boolean",
    category: "tuvisAdminFeatures",
    defaultValue: false,
  },
  hide_whatsapp_channel: {
    type: "boolean",
    category: "tuvisAdminChannels",
    defaultValue: false,
    hide: () => true,
    updatePermission: UserPermissions["waba.update_settings"] | UserPermissions["twilio.update_settings"] | UserPermissions["sidecars.update"],
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"],
  },
  hide_telegram_channel: {
    type: "boolean",
    category: "tuvisAdminChannels",
    defaultValue: true,
    hide: () => true,
    updatePermission: UserPermissions["waba.update_settings"] | UserPermissions["twilio.update_settings"] | UserPermissions["sidecars.update"],
    viewPermission:
      UserPermissions["waba.update_settings"] |
      UserPermissions["waba.view_settings"] |
      UserPermissions["twilio.update_settings"] |
      UserPermissions["twilio.view_settings"] |
      UserPermissions["sidecars.update"] |
      UserPermissions["sidecars.view"],
  },
  default_favorite_actions: {
    type: "multiSelect",
    defaultValue: DEFAULT_FAVORITES_ACTIONS,
    options: [
      "view",
      "call",
      "meeting",
      "messenger",
      "email",
      "navigate",
      "refresh",
      "facebook",
      "linkedin",
      "google",
      "find",
      "addToPhone",
      "createCustomObject",
      "createCase",
      "createTask",
      "createActivity",
      "createLead",
      "createContact",
      "createPersonAccount",
      "chatNow",
      "showChatSummarizeAction",
      "createBusinessAccount",
      "showAiChat",
    ],
    category: "actions",
  },
  disable_opportunities_default_layout_field_for_entities: {
    type: "multiSelect",
    defaultValue: [],
    category: "entity.create",
    options: ["task", "activity", "case"],
  },
  hide_intro_video: {
    type: "boolean",
    defaultValue: false,
    category: "notifications",
    hide: () => true,
  },
  use_apex_upload_files: {
    type: "boolean",
    category: "tuvisAdminFeatures",
    defaultValue: false,
  },
  allow_customer_facing_settings: {
    type: "boolean",
    category: "tuvisAdminFeatures",
    defaultValue: false,
  },
  allow_public_api: {
    type: "boolean",
    category: "tuvisAdminFeatures",
    defaultValue: false,
  },
  allow_url_policy: {
    type: "boolean",
    category: "tuvisAdminSecurity",
    defaultValue: false,
  },
  created_entities_title_prefix: {
    type: "string",
    category: "entity.create",
    defaultValue: PREFIX_SF_TUVIS_SUBJECT,
  },
  force_user_phone: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminUserManagement",
    conflictingKeys: ["desktop_add_paired_phone_on_login", "replace_phone_on_limit_exceeded"],
  },
  hide_edit_user: {
    type: "boolean",
    defaultValue: false,
    category: "general",
  },
  hide_logout: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminUserManagement",
  },
  incidents_summary_recipients: {
    type: "other",
    category: "tuvisAdminSecurity",
    hide: () => true,
    viewPermission: UserPermissions["incident.update"],
    updatePermission: UserPermissions["incident.update"],
  },
  fraud_detection: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  phishing_fraud_detection: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
    hide: (orgConfigs) => !orgConfigs.fraud_detection,
  },
  enable_virustotal_scan_url: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
    hide: (orgConfigs) => !orgConfigs.phishing_fraud_detection,
  },
  enable_hybrid_analysis_scan_url: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
    hide: (orgConfigs) => !orgConfigs.phishing_fraud_detection,
  },
  fraud_detection_create_incidents: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
    hide: (orgConfigs) => !orgConfigs.fraud_detection,
  },
  fraud_detection_incident_email_recipients: {
    type: "perUserObject",
    objectSubType: "boolean",
    category: "tuvisAdminSecurity",
    hide: (orgConfigs) => !orgConfigs.fraud_detection,
  },
  allow_tuvis_contacts: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["tuvis_contacts.view"],
    onlySuperAdminCanUpdate: true,
  },
  allow_keyword_insights: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["destinations.view"] | UserPermissions["analytics.view"],
    relatedReporters: ["tuvisKeywordInsights"],
  },
  allow_user_analytics: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["analytics.view"],
  },
  analytics_compliance: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["compliance.view"],
    onlySuperAdminCanUpdate: true,
  },
  allow_keyword_insights_report_message_content: {
    type: "boolean",
    defaultValue: true,
    category: "orgAdminRules",
  },
  allow_phones_whitelist: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions.none,
    onlySuperAdminCanUpdate: true,
  },
  allow_phones_blocklist: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions.none,
    onlySuperAdminCanUpdate: true,
  },
  enable_call_recording_disclaimer: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  custom_user_fields: {
    type: "stringArray",
    category: "orgAdminUserManagement",
    defaultValue: [],
    viewPermission: UserPermissions["user_management.view"],
    maxSelectedItems: 5,
    onlyUniqueItems: true,
  },
  collect_messages_group_participants: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  dlp_show_false_positive_incidents: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  dlp_notify_email_on_false_positive: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  query_cache_ttl_per_user: {
    type: "perUserObject",
    objectSubType: "numeric",
    category: "tuvisAdminFeatures",
  },
  allow_whatsapp_channels: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminMessagesSync",
  },
  sidecar_ebs_size_in_gb: {
    type: "numeric",
    defaultValue: 1,
    category: "tuvisAdminChannels",
    hide: (_, user) => !user?.userId || !DEVELOPERS_USER_IDS_FOR_SUPER_SENSITIVE_CONFIGS.includes(user.userId),
    onlySuperAdminCanUpdate: true,
  },
  incidents_filter_matched_keyword_campaigns_default_value: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  sidecar_ebs_size_in_gb_per_user: {
    type: "perUserObject",
    objectSubType: "numeric",
    category: "tuvisAdminChannels",
    hide: (_, user) => !user?.userId || !DEVELOPERS_USER_IDS_FOR_SUPER_SENSITIVE_CONFIGS.includes(user.userId),
    onlySuperAdminCanUpdate: true,
  },
  collect_whatsapp_status_messages: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminMessagesSync",
  },
  block_copy_paste: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminSecurity",
  },
  salesforce_use_browser: {
    type: "boolean",
    defaultValue: false,
    category: "apps",
  },
  sidecar_snapshot_ttl_days: {
    type: "numeric",
    defaultValue: 7,
    category: "tuvisAdminChannels",
    onlySuperAdminCanUpdate: true,
  },
  sidecar_snapshot_keep_count: {
    type: "numeric",
    defaultValue: 3,
    category: "tuvisAdminChannels",
    onlySuperAdminCanUpdate: true,
  },
  restrict_to_direct_reports: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  restrict_to_direct_reports_bypass_list: {
    type: "perUserObject",
    objectSubType: "boolean",
    category: "orgAdminRules",
    viewPermission: UserPermissions["user_management.view"],
  },
  view_only_own_messages: {
    type: "perUserObject",
    objectSubType: "boolean",
    category: "orgAdminRules",
    viewPermission: UserPermissions["user_management.view"],
  },
  multiple_records_extra_fields: {
    type: "stringArray",
    category: "actions",
  },
  allow_slack_platform: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
  },
  allow_teams_platform: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
  },
  allow_archive: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
  },
  // We need to add a way to make it so when allow_arcive is false, allow_teams_archive is also false automatically
  allow_teams_archive: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminChannels",
    hide: (orgConfigs) => !orgConfigs.allow_archive,
  },
  disable_internal_messages_sync: {
    type: "boolean",
    defaultValue: false,
    category: "orgAdminRules",
  },
  track_filter_analytics: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  sync_whatsapp_voice_video: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    hide: (orgConfigs) => !orgConfigs.enable_whatsapp_voice_video,
  },
  enable_whatsapp_voice_video: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
  },
  allow_bot_builder: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["bot_builder.view"] | UserPermissions["bot_builder.update"],
  },
  allow_elite_token: {
    type: "boolean",
    defaultValue: false,
    category: "tuvisAdminFeatures",
    viewPermission: UserPermissions["elite_token.view"] | UserPermissions["elite_token.update"],
  },
  botSessionHistoryRetentionDays: {
    type: "numeric",
    defaultValue: 30,
    minNumber: 7,
    maxNumber: 365,
    category: "tuvisAdminFeatures",
    updatePermission: UserPermissions["bot_builder.update"],
    viewPermission: UserPermissions["bot_builder.view"] | UserPermissions["bot_builder.update"],
    hide: () => true,
  },
};

export const orgConfigFeaturesArray: OrgConfigFeaturesType[] = Object.entries(orgConfigsObject).map<OrgConfigFeaturesType>(([key, settings]) => ({
  ...settings,
  key,
}));

export function clampNumericValue(value: unknown, configDef: { minNumber?: number; maxNumber?: number; defaultValue?: unknown }): number {
  const defaultNum = typeof configDef.defaultValue === "number" ? configDef.defaultValue : 0;
  const numericValue = typeof value === "number" && !isNaN(value) ? value : defaultNum;
  return Math.min(configDef.maxNumber ?? Infinity, Math.max(configDef.minNumber ?? -Infinity, numericValue));
}

/** Keys with `hide: () => true` in orgConfigsObject (Settings UI only). Keep in sync with `shouldIgnoreFeature` exemption in SuperAdminOrgConfigsStore. */
export const ORG_IDENTITY_MODAL_CONFIG_KEYS = [
  "available_crm",
  "defaultLanguage",
  "org_partner",
  "isActive",
  "isPOC",
  "serverlocation",
  "orgType",
  "POCExpiration",
] as const;

export type OrgIdentityModalConfigKey = (typeof ORG_IDENTITY_MODAL_CONFIG_KEYS)[number];
